import styled from 'styled-components'
import { tokens } from 'styling/tokens'

interface GlobalViewToggleProsp {
  active: boolean
}

export const GlobalViewToggle = styled.div<GlobalViewToggleProsp>`
  min-width: 60px;
  height: 60px;
  box-shadow: 0px 0px 25px rgba(0, 0, 0, 0.1);
  border-radius: 12.5px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  svg {
    fill: ${(props) =>
      props.active ? tokens.accent.base : tokens.text.secondary} !important;
    height: 35px;
    width: 35px;
  }
`

export const GlobalViewControls = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;

  @media (max-width: 500px) {
    display: none;
  }
`

export const GlobalViewMobileMenu = styled.div`
  display: none;
  position: relative;
  min-width: 60px;
  width: 60px;
  height: 60px;
  margin-left: 1rem;
  box-shadow: 0px 0px 25px rgba(0, 0, 0, 0.1);
  border-radius: 12.5px;
  align-items: center;
  justify-content: center;
  svg {
    fill: ${tokens.text.primary} !important;
    height: 35px;
    width: 35px;
  }

  @media (max-width: 500px) {
    display: flex;
  }
`

export const GlobalViewMobileOptions = styled.div<{ open?: boolean }>`
  position: absolute;
  top: 125%;
  right: 0;
  width: fit-content;
  height: max-content;
  display: flex;
  flex-direction: column;
  transition: 0.2s ease;
  padding: 1rem;
  ${({ open }) => (open ? 'min-height:11rem; height:20rem' : 'height:0; padding:0')};
  overflow: hidden;
  background-color: ${tokens.surface.raised};
  z-index: 999;
  border-radius: ${tokens.radius.card};
  gap: 1rem;
  * {
    z-index: 9999;
  }
`

/** Matches satellite imagery base tone so loading never flashes white/gray before tiles appear */
export const MAP_BASE_BACKGROUND = '#1e2126'

export const GlobalViewMapContainer = styled.div`
  width: 100%;
  height: 100vh;
  border-radius: ${tokens.radius.card};
  background-color: ${MAP_BASE_BACKGROUND};
`

/** Shown while Google Maps JS API is loading (Wrapper has no map div yet) */
export const MapLoadingPlaceholder = styled.div`
  width: 100%;
  height: 100vh;
  border-radius: ${tokens.radius.card};
  background-color: ${MAP_BASE_BACKGROUND};
`

export const GlobalViewSelect = styled.div`
  font-weight: 600;
  font-size: 11.6688px;
  background: ${tokens.surface.raised};
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1rem;
  box-shadow: ${tokens.shadow.card};
  border-radius: ${tokens.radius.control};
  min-width: 140px;
  height: 60px;
  position: relative;
  cursor: pointer;
`

export const GlobalViewOptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: center;
  position: absolute;
  top: 110%;
  left: 0;
  z-index: 9;
`

export const GlobalViewSelectOption = styled.div`
  cursor: pointer;
  background: ${tokens.surface.raised};
  box-shadow: ${tokens.shadow.card};
  border-radius: ${tokens.radius.control};
  width: 140px;
  height: 40px;
  display: flex;
  align-items: center;
  padding: 20px;

  &:hover {
    color: ${tokens.accent.base};
  }
`

export const GlobalViewContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: space-between;
  position: absolute;
  padding: 6rem 1rem;
  padding-bottom: 1rem;
  left: 0;
  bottom: 0;
  gap: 2rem;
`

export const GlobalViewFilterContainer = styled.div<{ open?: boolean }>`
  ${({ open }) => (open ? 'left:1rem;opacity:1;' : 'left:-100%;opacity:0;')}
  position: absolute;
  border-radius: ${tokens.radius.card};
  width: 250px;
  height: 88vh;
  transition: 0.2s ease-out;
  z-index: 999;
  padding: 1rem;
  ${(props) => props.theme.glassCard}
  .title {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
`

export const GlobalViewWidgetsContainer = styled.div<{ closed?: boolean }>`
  ${({ closed }) => (closed ? 'transform:translateX(-100%);opacity:0;' : '')}
  transition: .2s ease-out;
  display: flex;
  flex-direction: column;
  min-width: 170px;
  gap: 10px;
  overflow: auto;
  z-index: 9999;
  height: 88vh;
  padding-bottom: 1rem;

  &::-webkit-scrollbar {
    display: none;
  }

  /* Hide scrollbar for IE, Edge and Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
`

export const GlobalViewDetailsContainer = styled(GlobalViewWidgetsContainer)`
  ${({ closed }) => (closed ? 'transform:translateX(110%);opacity:0;' : 'transform:translateX(0);')}
  position: absolute;
  right: 1rem;
  min-width: 330px;
  max-width: 330px;
  padding: 1rem;
  padding-top: 5.5rem;
  opacity: 1;
  ${(props) => props.theme.container};
  overflow: visible;
`

export const GLDetailsHeader = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 84px;
  border-top-left-radius: ${tokens.radius.card};
  border-top-right-radius: ${tokens.radius.card};
  background: ${tokens.surface.raised};
  border-bottom: 1px solid ${tokens.border.hairline};
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
`

export const GLDetailsHeaderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  .text {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
`

export const GLAvatar = styled.div<{ $machine?: boolean }>`
  width: 2.75rem;
  height: 2.75rem;
  flex: none;
  border-radius: ${(props) => (props.$machine ? tokens.radius.control : '50%')};
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${tokens.surface.inset};
  border: 1px solid ${tokens.border.hairline};
  color: ${tokens.text.secondary};
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  svg {
    width: 1.35rem;
    height: 1.35rem;
  }
`

export const GLDetailsCardContainer = styled.div`
  height: 100%;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;
  transition: scrollbar-color 0.2s ease;
  &:hover {
    scrollbar-color: rgba(255, 255, 255, 0.24) transparent;
  }
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
    margin: 0.25rem 0;
  }
  &::-webkit-scrollbar-thumb {
    background: transparent;
    border-radius: 999px;
    transition: background 0.2s ease;
  }
  &:hover::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.24);
  }
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.36);
  }
  position: relative;
`

export const GLDetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: repeat(2, 1fr);
  grid-column-gap: 0px;
  grid-row-gap: 5px;
`

export const GLDetailsStartEnd = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 1rem;
  div {
    display: flex;
    flex-direction: column;
  }
`

export const GLDetailsCard = styled.div`
  /* Dropdown */
  z-index: 999;
  width: 100%;
  height: max-content;
  padding: 0.875rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.625rem;

  border-radius: ${tokens.radius.card};
  background: ${tokens.surface.raised};
  border: 1px solid ${tokens.border.hairline};

  -ms-overflow-style: none; /* Internet Explorer 10+ */
  scrollbar-width: none; /* Firefox */

  &::-webkit-scrollbar {
    display: none; /* Safari and Chrome */
  }
`

export const GLDetailsHeaderRating = styled.div`
  display: flex;
  gap: 5px;
  align-items: center;
  height: min-content;
`

export const GLDonut = styled.div<{ $gradient: string }>`
  width: 7rem;
  height: 7rem;
  flex: none;
  border-radius: 50%;
  background: ${(props) => props.$gradient};
  display: flex;
  align-items: center;
  justify-content: center;
`

export const GLDonutHole = styled.div`
  width: 5rem;
  height: 5rem;
  border-radius: 50%;
  background: ${tokens.surface.raised};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
`

export const GLDonutValue = styled.span<{ $muted?: boolean }>`
  font-size: 0.95rem;
  font-weight: 600;
  color: ${(props) => (props.$muted ? tokens.text.muted : tokens.text.primary)};
  font-variant-numeric: tabular-nums;
`

export const GLDonutSub = styled.span`
  font-size: 9px;
  color: ${tokens.text.muted};
  letter-spacing: 0.04em;
  font-style: italic;
`

export const GlobalViewWidget = styled.div`
  min-width: 170px;
  padding: 0.875rem;
  border-radius: ${tokens.radius.card};
  transition: 0.2s ease-in;
  display: flex;
  flex-direction: column;
  ${(props) => props.theme.container};
`

export const GLWidgetRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  margin-top: 0.625rem;
`

export const GloblViewWidgetTitle = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`

export const GlobalViewWidgetContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 1rem;
`

export const WidgetBarContainer = styled.div<{ selected?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  border-radius: ${tokens.radius.chip};
  border: 1px solid transparent;
  transition: background 0.12s ease, border-color 0.12s ease;
  &:hover {
    background: ${tokens.surface.inset};
  }
  ${(props) =>
    props.selected && `background:${tokens.accent.soft}; border-color:${tokens.accent.base};`}
`

export const WidgetBarInfoContainer = styled.div`
  display: flex;
  width: 100%;
  gap: 8px;
  flex-direction: column;
`

export const WidgetBar = styled.div<{ color: string; width?: string }>`
  width: ${(props) => props.width || '10px'};
  position: relative;
  background: ${(props) => props.color};
  height: 10px;
  border-radius: 3px;
  backdrop-filter: blur(13.2px);

  &::after {
    content: '';
    background: ${(props) => props.color};
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    background: ${(props) => props.color};
    border-radius: 3px;
    filter: blur(2.7px);
  }
`

export const AskAiContainer = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
  align-self: flex-end;
`

export const AskAiButton = styled.div<{ width?: string; cursor?: string }>`
  width: ${(props) => props.width || '150px'};
  height: 50px;
  justify-content: center;
  display: flex;
  align-items: center;
  cursor: ${(props) => props.cursor || 'pointer'};
  box-sizing: border-box;
  border-radius: ${tokens.radius.control};
  transition: 0.2s ease;
  padding: 0 1rem;
  ${(props) => props.theme.glassCard}
  &:hover {
    background: ${(props) => props.theme.glassHover};
  }

  input {
    width: 100%;
    height: 100%;
    background: transparent;
    border: none;
    outline: none;
    color: ${tokens.text.primary};
    font-size: 16px;
    padding-left: 10px;
    &::placeholder {
      color: ${tokens.text.primary};
      opacity: 0.5;
    }

    ::-ms-input-placeholder {
      /* Edge 12 -18 */
      color: ${tokens.text.muted};
    }
  }
`

export const AIChatContainer = styled.div<{ open?: boolean; darken?: boolean }>`
  ${({ open }) => (open ? 'height: 450px;padding:1rem;' : 'height: 0px;padding:0;')};
  overflow: hidden;
  transition: 0.3s ease-in;
  position: absolute;
  left: 0;
  bottom: 110%;
  width: 100%;
  max-height: 450px;
  display: flex;
  gap: 1rem;
  z-index: 999;
  background: ${(props) => (props.darken ? 'rgba(34, 32, 32, 0.7)' : 'rgba(34, 32, 32, 0.3)')};
  box-shadow: ${tokens.shadow.card};
  backdrop-filter: blur(2.845px);
  border-radius: ${tokens.radius.card};
`

export const AIChatHistory = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  overflow: auto;
  width: 30%;
  padding: 0 15px;
`

export const AIChatHistoryItem = styled.div`
  color: ${tokens.text.primary};
  min-height: 28px;
  display: flex;
  align-items: center;
  width: 100%;
  cursor: pointer;
  animation: 0.2s ease-in;
  padding: 5px 10px;
  p {
    font-size: 13px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &:hover {
    background: rgba(34, 32, 32, 0.3);
    box-shadow: ${tokens.shadow.card};
    backdrop-filter: blur(2.845px);
    border-radius: ${tokens.radius.chip};
  }
`

export const AICurrentChat = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  overflow: auto;
  width: 70%;
`

export const AIChatResponse = styled.div`
  display: flex;
  p {
    color: ${tokens.text.primary};
  }
`

export const AIChatQuestion = styled.div`
  width: auto;
  display: flex;
  justify-content: flex-end;
  width: 100%;

  .content {
    /* Dropdown */
    ${(props) => props.theme.glassCard}
    border-radius: ${tokens.radius.card};
    padding: 1rem;
  }
`

export const SelectedPins = styled.div<{ closed: boolean }>`
  // TODO nasledi isti div
  ${({ closed }) => (closed ? 'transform:translateX(100%);opacity:0;pointer-events:none;' : '')}
  transition: .2s ease-out;
  display: flex;
  flex-direction: column;
  align-items: end;
  min-width: 105px;
  gap: 10px;
  z-index: 9999;
  height: 88vh;

  &::-webkit-scrollbar {
    display: none;
  }

  /* Hide scrollbar for IE, Edge and Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
`

export const SelectedPinContainer = styled.div`
  position: relative;
  border-radius: ${tokens.radius.card};
  width: 98px;
  min-height: 108px;
  padding: 0.875rem 0.5rem;
  height: fit-content;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.625rem;
  background: ${tokens.surface.glass};
  backdrop-filter: blur(${tokens.blur});
  border: 1px solid ${tokens.border.hairline};
  box-shadow: ${tokens.shadow.card};
  overflow: visible;
`

export const SelectedPinName = styled.span`
  font-size: 0.7rem;
  font-weight: 500;
  color: ${tokens.text.primary};
  text-align: center;
  line-height: 1.3;
  width: 100%;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

export const SelectedPinAction = styled.div`
  cursor: pointer;
  position: absolute;
  left: -15px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${tokens.surface.raised};
  border: 1px solid ${tokens.border.hairline};
  box-shadow: ${tokens.shadow.card};
  width: 30px;
  height: 30px;
  border-radius: ${tokens.radius.control};
  z-index: 9999;
  transition: background 0.15s ease;
  &:hover {
    background: ${tokens.surface.inset};
  }
`

export const ShiftTimelineContainer = styled.div`
  display: flex;
  height: 10px;
  overflow: hidden;
  margin-bottom: 8px;
  gap: 2px;
  position: relative;
  padding: 0 2px;

  .line {
    position: absolute;
    width: 100%;
    height: 1px;
    border-radius: 2px;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    background-color: ${tokens.text.secondary};
    z-index: 1;
  }
`

export const ShiftTimelineSegment = styled.div<{ type: 'task' | 'free'; width: string }>`
  width: ${(props) => props.width}%;
  background-color: ${(props) =>
    props.type == 'task' ? tokens.accent.base : tokens.status.warning.fg};
  opacity: 0.74;
  border-radius: 3px;
  z-index: 3;
`

export const ShiftTimeLegend = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: center;
  margin-top: 1rem;
`

export const LegendColor = styled.div<{ color?: string }>`
  width: 4px;
  height: 4px;
  border-radius: 1px;
  background-color: ${(props) => props.color};
`

export const TripCard = styled.div`
  width: 100%;
  ${(props) => props.theme.container};
  border: 1px solid ${tokens.border.hairline};
  cursor: pointer;

  .top {
    border-bottom: 1px solid ${tokens.border.hairline};
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem;
  }

  .bottom {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 1rem;
  }
`
